import { makeStyles, mergeClasses, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, tokens } from "@fluentui/react-components";
import { useMemo, useState } from "react";
import { AppDialog, DialogActionsBar } from "@/components/app-dialog";
import { CompactKpi } from "@/components/compact-kpi";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { usePortfolioStyles } from "./PortfolioViews";
import { SegmentedControl } from "./SegmentedControl";
import type { OrgImportPreview, OrgIssue } from "./org-excel-preview";

const useStyles = makeStyles({
  kpis: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalS,
  },
  tableWrap: {
    maxHeight: "360px",
    overflow: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  cell: {
    padding: "6px 8px",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    verticalAlign: "middle",
  },
  num: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
  },
  warning: {
    color: tokens.colorPaletteDarkOrangeForeground1,
  },
  truncate: {
    maxWidth: "220px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

type Tab = "apply" | "errors" | "warnings";

function actionLabel(action: string): string {
  if (action === "create") return "신규";
  if (action === "update") return "수정";
  if (action === "unchanged") return "변경 없음";
  return "건너뜀";
}

function IssueTable({
  rows,
  emptyTitle,
  emptyDescription,
}: {
  rows: OrgIssue[];
  emptyTitle: string;
  emptyDescription: string;
}) {
  const styles = useStyles();
  const pStyles = usePortfolioStyles();
  if (rows.length === 0) {
    return <GhostEmptyState compact title={emptyTitle} description={emptyDescription} />;
  }
  return (
    <div className={styles.tableWrap}>
      <Table size="small" className={mergeClasses(styles.table, pStyles.fluent)} aria-label="유효성 결과">
        <TableHeader>
          <TableRow className={pStyles.headRow}>
            <TableHeaderCell className={mergeClasses(pStyles.head, styles.cell)}>시트</TableHeaderCell>
            <TableHeaderCell className={mergeClasses(pStyles.head, styles.cell, styles.num)}>행</TableHeaderCell>
            <TableHeaderCell className={mergeClasses(pStyles.head, styles.cell)}>필드</TableHeaderCell>
            <TableHeaderCell className={mergeClasses(pStyles.head, styles.cell)}>입력값</TableHeaderCell>
            <TableHeaderCell className={mergeClasses(pStyles.head, styles.cell)}>사유</TableHeaderCell>
            <TableHeaderCell className={mergeClasses(pStyles.head, styles.cell)}>처리</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={`${row.sheet}-${row.excelRow}-${row.field}-${i}`}>
              <TableCell className={styles.cell}>{row.sheet}</TableCell>
              <TableCell className={mergeClasses(styles.cell, styles.num)}>{row.excelRow || "—"}</TableCell>
              <TableCell className={styles.cell}>{row.field}</TableCell>
              <TableCell className={mergeClasses(styles.cell, styles.truncate)} title={row.value}>
                {row.value || "—"}
              </TableCell>
              <TableCell className={mergeClasses(styles.cell, row.severity === "error" ? styles.error : styles.warning)}>{row.message}</TableCell>
              <TableCell className={mergeClasses(styles.cell, styles.muted)}>{row.action}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function OrgExcelPreviewDialog({
  open,
  preview,
  applying,
  onClose,
  onApply,
}: {
  open: boolean;
  preview: OrgImportPreview | null;
  applying?: boolean;
  onClose: () => void;
  onApply: () => void;
}) {
  const styles = useStyles();
  const pStyles = usePortfolioStyles();
  const [tab, setTab] = useState<Tab>("apply");

  const applyRows = useMemo(() => {
    if (!preview) return [];
    return [
      ...preview.teams
        .filter((r) => r.action === "create" || r.action === "update")
        .map((r) => ({ sheet: "팀", excelRow: r.excelRow, name: r.name, action: r.action, fields: r.changedFields.join(", ") })),
      ...preview.people
        .filter((r) => r.action === "create" || r.action === "update")
        .map((r) => ({ sheet: "인력", excelRow: r.excelRow, name: r.displayName, action: r.action, fields: r.changedFields.join(", ") })),
    ];
  }, [preview]);

  const errors = preview?.issues.filter((i) => i.severity === "error") ?? [];
  const warnings = preview?.issues.filter((i) => i.severity === "warning") ?? [];
  const canApply = Boolean(preview && !preview.blocking && preview.applyableCount > 0);

  const confirmLabel = preview?.blocking
    ? "적용할 수 없음"
    : preview && preview.errorCount > 0
      ? `유효한 ${preview.applyableCount}행 적용 (오류 ${preview.errorCount}건 제외)`
      : `유효한 ${preview?.applyableCount ?? 0}행 적용`;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      width="xl"
      height={560}
      title="엑셀 적용 미리보기"
      description={preview ? `${preview.fileName} · 오류 행은 적용하지 않습니다.` : "파일을 확인하는 중입니다."}
      className="max-w-[min(960px,calc(100%-2rem))]"
      footerStart={preview?.blocking ? "스키마나 필수 탭을 먼저 고쳐 주세요." : undefined}
      footer={
        <DialogActionsBar
          onCancel={onClose}
          onConfirm={onApply}
          confirmLabel={applying ? "적용 중" : confirmLabel}
          disabled={!canApply || applying}
        />
      }
    >
      {preview && (
        <>
          <div className={styles.kpis}>
            <CompactKpi label="적용 가능" value={preview.applyableCount} unit="행" tone="brand" onClick={() => setTab("apply")} />
            <CompactKpi label="신규 / 수정" value={`${preview.createCount} / ${preview.updateCount}`} tone="muted" />
            <CompactKpi label="오류" value={preview.errorCount} unit="건" tone={preview.errorCount ? "danger" : "muted"} onClick={() => setTab("errors")} />
            <CompactKpi label="경고" value={preview.warningCount} unit="건" tone={preview.warningCount ? "warning" : "muted"} onClick={() => setTab("warnings")} />
          </div>
          <SegmentedControl
            value={tab}
            onChange={(v) => setTab(v as Tab)}
            ariaLabel="미리보기 구분"
            options={[
              { value: "apply", label: `적용 예정 ${applyRows.length}` },
              { value: "errors", label: `오류 ${errors.length}` },
              { value: "warnings", label: `경고 ${warnings.length}` },
            ]}
          />
          {tab === "apply" &&
            (applyRows.length === 0 ? (
              <GhostEmptyState compact title="적용할 변경이 없습니다" description="오류를 고치거나, 신규·수정 행이 있는 파일을 올려 주세요." />
            ) : (
              <div className={styles.tableWrap}>
                <Table size="small" className={mergeClasses(styles.table, pStyles.fluent)} aria-label="적용 예정">
                  <TableHeader>
                    <TableRow className={pStyles.headRow}>
                      <TableHeaderCell className={mergeClasses(pStyles.head, styles.cell)}>시트</TableHeaderCell>
                      <TableHeaderCell className={mergeClasses(pStyles.head, styles.cell, styles.num)}>행</TableHeaderCell>
                      <TableHeaderCell className={mergeClasses(pStyles.head, styles.cell)}>항목</TableHeaderCell>
                      <TableHeaderCell className={mergeClasses(pStyles.head, styles.cell)}>처리</TableHeaderCell>
                      <TableHeaderCell className={mergeClasses(pStyles.head, styles.cell)}>변경</TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {applyRows.map((row) => (
                      <TableRow key={`${row.sheet}-${row.excelRow}-${row.name}`}>
                        <TableCell className={styles.cell}>{row.sheet}</TableCell>
                        <TableCell className={mergeClasses(styles.cell, styles.num)}>{row.excelRow}</TableCell>
                        <TableCell className={mergeClasses(styles.cell, styles.truncate)} title={row.name}>
                          {row.name}
                        </TableCell>
                        <TableCell className={styles.cell}>{actionLabel(row.action)}</TableCell>
                        <TableCell className={mergeClasses(styles.cell, styles.muted)}>{row.fields}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))}
          {tab === "errors" && <IssueTable rows={errors} emptyTitle="오류가 없습니다" emptyDescription="이 파일의 행은 형식 검사를 통과했습니다." />}
          {tab === "warnings" && <IssueTable rows={warnings} emptyTitle="경고가 없습니다" emptyDescription="무시할 값이나 참고 시트 변경이 없습니다." />}
        </>
      )}
    </AppDialog>
  );
}
